
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { followUser, getFollowingFeed, searchUsers, unfollowUser } from '../../../api/client';
import type { ActivityFeedItem, AppUserApi } from '../../../api/client';

export default function SubscriberList() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'following' | 'online'>('all');
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<AppUserApi[]>([]);
  const [feed, setFeed] = useState<ActivityFeedItem[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshUsers = async () => {
    const data = await searchUsers({ search: query, filter });
    setUsers(data);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([searchUsers({ search: query, filter }), getFollowingFeed()])
      .then(([list, feedItems]) => {
        if (cancelled) return;
        setUsers(list);
        setFeed(feedItems.slice(0, 8));
      })
      .catch(() => {
        if (!cancelled) {
          setUsers([]);
          setFeed([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter, query]);

  const toggleFollow = async (user: AppUserApi) => {
    try {
      if (user.is_following) await unfollowUser(user.id);
      else await followUser(user.id);
      await refreshUsers();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm">
        <div className="flex gap-2 mb-3">
          <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === 'all' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All</button>
          <button onClick={() => setFilter('following')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === 'following' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Following</button>
          <button onClick={() => setFilter('online')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === 'online' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Online</button>
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search people..."
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-500"
        />
      </div>

      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm">
        <h2 className="text-lg font-bold text-gray-800 mb-4">People</h2>
        {loading ? (
          <p className="text-sm text-gray-500 py-4">Loading users...</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No users found.</p>
        ) : (
          <div className="space-y-4">
            {users.map((user) => (
              <div key={user.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <button
                  type="button"
                  onClick={() => navigate(`/u/${encodeURIComponent(user.username)}`)}
                  className="relative shrink-0"
                >
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt={user.display_name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center">
                      <i className="ri-user-line text-stone-500" />
                    </div>
                  )}
                  {user.is_online && <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full" />}
                </button>
                <div className="flex-1 min-w-0">
                  <button type="button" onClick={() => navigate(`/u/${encodeURIComponent(user.username)}`)} className="font-medium text-sm text-gray-800 truncate hover:text-orange-700">
                    {user.display_name}
                  </button>
                  <p className="text-xs text-gray-600">@{user.username}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {user.followers_count} followers
                    {user.now_playing ? ` • Listening: ${user.now_playing}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => toggleFollow(user)}
                  className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${
                    user.is_following ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-orange-600 text-white hover:bg-orange-700'
                  }`}
                >
                  {user.is_following ? 'Following' : 'Follow'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Following Feed</h2>
        {feed.length === 0 ? (
          <p className="text-sm text-gray-500">Follow people to see activity.</p>
        ) : (
          <div className="space-y-3">
            {feed.map((item) => (
              <div key={item.id} className="p-3 rounded-xl bg-gray-50">
                <p className="text-sm text-gray-800">
                  <span className="font-medium">{item.actor_display_name || item.actor_username}</span>{' '}
                  <span className="text-gray-600">{item.activity_type.replaceAll('.', ' ')}</span>
                  {item.playlist_name ? ` • ${item.playlist_name}` : ''}
                  {item.track_title ? ` • ${item.track_title}` : ''}
                </p>
                <p className="text-xs text-gray-500 mt-1">{new Date(item.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
